import React, { useCallback, useMemo, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import ItemContent from '../../../components/ItemContent/ItemContent';
import { IEditForm } from '../../../types/interfaces/IEditForm';
import { EStatus } from '../../../types/enums/EStatus';
import { useTranslation } from 'react-i18next';
import tPaths from '../../../utils/tPaths';
import {
  CAMPAIGN_CODE_TYPE_OPTIONS,
  CAMPAIGN_TYPE_OPTIONS,
  ECampaignCodeType,
  NSPromoCampaigns,
} from '../../../types/interfaces/NSPromoCampaigns';
import { EPaths } from '../../../types/enums/EPaths';
import { SessionContext } from '../../../contexts/session/SessionContext';
import useApi from '../../../utils/useApi';
import { endpoints } from '../../../utils/endpoints';

interface IData {
  carrierIdentifier: string;
  name: string;
}

const EditPromoCampaign = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data: packages } = useApi<IData[]>(endpoints.promoCampaignsPackages);

  const { permissions } = useContext(SessionContext);

  const dataMapper = useCallback(
    (data: NSPromoCampaigns.IDetails): IEditForm[][] => {
      console.log('data', data);
      return [
        [
          {
            type: 'input',
            fieldName: 'internalName',
            label: t(tPaths.editFormLabels.internalName),
            initialValue: data.internalName,
          },
          {
            type: 'select',
            fieldName: 'codeType',
            label: t(tPaths.editFormLabels.genericUniqueCode),
            variants: CAMPAIGN_CODE_TYPE_OPTIONS,
            initialValue: data.codeType,
            disabled: true,
          },
          {
            type: 'input',
            fieldName: 'publicCode',
            label: t(tPaths.editFormLabels.publicCode),
            hidden: data.codeType !== ECampaignCodeType.Generic,
            initialValue: data.publicCode,
          },
          {
            type: 'codesGeneration',
            fieldName: 'promoCodePrefix',
            hidden: data.codeType !== ECampaignCodeType.Unique,
            initialValue: data.promoCodePrefix,
          },
          {
            type: 'select',
            fieldName: 'package',
            label: t(tPaths.editFormLabels.package),
            variants: packages ? packages?.map((item) => ({ value: item.carrierIdentifier, label: item.name })) : [],
            initialValue: data.targetPackageId,
            disabled: !!data.activationDate,
          },
          {
            type: 'select',
            fieldName: 'type',
            label: t(tPaths.editFormLabels.campaignType),
            variants: CAMPAIGN_TYPE_OPTIONS,
            initialValue: data.type,
            disabled: !!data.activationDate,
          },
          {
            type: 'input',
            fieldName: 'technicalCode',
            label: t(tPaths.editFormLabels.technicalCode),
            initialValue: data.technicalCode,
          },
          {
            type: 'input',
            fieldName: 'affiliateCode',
            label: t(tPaths.editFormLabels.affiliateCode),
            initialValue: data.affiliateCode,
          },
          {
            type: 'switch',
            fieldName: 'oncePerDevice',
            label: t(tPaths.editFormLabels.oncePerDevice),
            initialValue: data.oncePerDevice,
            disabled: !!data.activationDate,
          },
          {
            type: 'switch',
            fieldName: 'oncePerUser',
            label: t(tPaths.editFormLabels.oncePerUser),
            initialValue: data.oncePerUser,
            disabled: !!data.activationDate,
          },
        ],
      ];
    },
    [t, packages],
  );

  const updateDataFormatter = useCallback(
    (fields: any) => ({
      internalName: fields.internalName.value,
      codeType: fields.codeType.value,
      publicCode: fields.publicCode.value,
      technicalCode: fields.technicalCode.value,
      affiliateCode: fields.affiliateCode.value,
      type: fields.type.value,
      targetPackageId: fields.package.value,
      oncePerUser: fields.oncePerUser.value,
      oncePerDevice: fields.oncePerDevice.value,
      promoCodePrefix: fields.promoCodePrefix.value,
      status: fields.status ? EStatus.Active : EStatus.Inactive,
    }),
    [],
  );

  const switches = useMemo(() => [{ key: 'status', label: t(tPaths.itemContent.active) }], [t]);
  const switchesDataMapper = useCallback((data: NSPromoCampaigns.IDetails) => {
    return { status: data.status.current === EStatus.Active };
  }, []);

  return (
    <ItemContent
      itemId={id}
      title={t(tPaths.itemContent.editPromoCampaign)}
      hint={(data) => (data?.activationDate ? `Activated on ${data.activationDate}` : '')}
      path={EPaths.PromoCampaigns}
      contentMapper={dataMapper}
      updateDataFormatter={updateDataFormatter}
      switches={switches}
      switchesDataMapper={switchesDataMapper}
      editable={permissions.promoCampaigns.edit}
    />
  );
};

export default EditPromoCampaign;
